import { useEffect, useRef, useState } from "react";

/**
 * Letter-by-letter scramble that reveals the original text.
 * Triggered by the parent's hover/focus via the `trigger` prop.
 */
interface Props {
  text: string;
  trigger?: boolean;
  duration?: number;
  className?: string;
}

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

const ScrambleText = ({ text, trigger = false, duration = 480, className }: Props) => {
  const [display, setDisplay] = useState(text);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (!trigger) {
      setDisplay(text);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    startRef.current = performance.now();

    const step = (now: number) => {
      const elapsed = now - startRef.current;
      const progress = Math.min(1, elapsed / duration);
      // reveal index advances with progress
      const revealCount = Math.floor(progress * text.length);

      let out = "";
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === " ") {
          out += " ";
          continue;
        }
        if (i < revealCount) {
          out += ch;
        } else {
          // scramble, keep case roughly matching
          const c = CHARS[Math.floor(Math.random() * CHARS.length)];
          out += ch === ch.toLowerCase() ? c.toLowerCase() : c;
        }
      }
      setDisplay(out);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplay(text);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [trigger, text, duration]);

  return (
    <span
      className={className}
      style={{ fontVariantNumeric: "tabular-nums", fontFeatureSettings: '"tnum"' }}
    >
      {display}
    </span>
  );
};

export default ScrambleText;
